import { Box, Button, darken, Grid, Slide, useTheme, Typography } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { DateRange } from '@mui/x-date-pickers-pro';
import { tzdate, TZDate } from '@repo/tzdate';
import { Dayjs } from 'dayjs';
import { convertDateRangeToTZDate } from 'src/utils/dateRangeUtils';
import { createRef, useState, useContext, useMemo } from 'react';
import Popover from 'src/components/common/popover/Popover';
import InstancePicker from 'src/components/domain/instance-picker/InstancePicker';
import BookingTimeslots from 'src/components/domain/timeslots-booking/calendar/booking-timeslots/BookingTimeslots';
import { CalendarHeader } from 'src/components/domain/timeslots-booking/calendar/calendar-header/CalendarHeader';
import { useFocusTrap } from 'src/hooks/common/useFocusTrap';
import { useLocale } from '@repo/i18n';
import { ProductInstance, TicketOptionWithQuantity, Timeslot } from '@repo/types';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { capitalize } from '@repo/common-utils/TextUtils';
import { zIndex } from 'src/utils/widget/zIndex';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';
import {
    findAvailabilityData,
    findFirstAvailableProductForDate,
    getAllDatesInDateRange,
} from '@repo/widget-utils/DateHelpers';
import { TimeSlotType } from '@repo/widget-utils/TimeSlotType';
import StaticDateRangePicker from '../../date-range-picker/StaticDateRangePicker';
import BilberryStaticCalendar from '../BilberryStaticCalendar';

interface IProps {
    dateRangeVariant?: 'days' | 'nights';
    selectedDateRange: DateRange<TZDate> | undefined;
    onSelectDateRange: ((dateRange: DateRange<any>) => void) | undefined;
    minDate: TZDate;
    displayDate: TZDate | null;
    displayTimeslot: TimeSlotType | undefined;
    setDisplayTimeslot: React.Dispatch<React.SetStateAction<TimeSlotType | undefined>>;
    anchorEl: HTMLDivElement | HTMLInputElement | null;
    availabilityData: ProductInstance[];
    isLoading?: boolean;
    updateDisplayDate: (date: TZDate | null, findTimeslot?: boolean) => void;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLDivElement | HTMLInputElement | null>>;
    onSelectTimeSlot?: (timeslot: TimeSlotType | undefined) => void;
    setSelectedProducts?: (product: ProductInstance[] | undefined) => void;
    setAvailabilitySearchPeriod: (availabilitySearchPeriod: {
        startDay: TZDate | null;
        endDay: TZDate | null;
    }) => void;
    id?: string;
    quantities: TicketOptionWithQuantity[];
    setHasChosenDate: (hasChosenDate: boolean) => void;
    availableTimeslots?: Timeslot[];
}

export default function CalendarPopover(props: IProps) {
    const {
        dateRangeVariant,
        selectedDateRange,
        onSelectDateRange,
        minDate,
        displayDate,
        displayTimeslot,
        setDisplayTimeslot,
        anchorEl,
        availabilityData,
        updateDisplayDate,
        setAnchorEl,
        onSelectTimeSlot,
        setSelectedProducts,
        setAvailabilitySearchPeriod,
        id = 'bilberry-calendar-input',
        quantities,
        setHasChosenDate,
        availableTimeslots,
        isLoading,
    } = props;

    const mountPoint = useContext(MountPointContext);
    const popoverRef = createRef<HTMLDivElement>();
    const theme = useTheme();
    const { t } = useLocale();
    const config = useConfigurations();
    const threeYearsAhead = minDate.add(5, 'year');
    const trap = useFocusTrap(popoverRef, !!anchorEl, `#${id}`, mountPoint.shadowRoot);
    const [timeslotSelectorVisible, setTimeslotSelectorVisible] = useState<boolean>(false);
    const showWeekNumbers = config.showWeekNumbers ?? false;

    const updateAvailabilitySearchPeriod = (date: TZDate | null) => {
        if (!date) return;

        setAvailabilitySearchPeriod({
            startDay: date.startOf('month').startOf('day'),
            endDay: date.endOf('month').add(1, 'day').startOf('day'),
        });
    };

    const onSelectDay = (date: TZDate | null) => {
        updateDisplayDate(date, true);
    };

    const onClose = () => {
        if (trap) trap.deactivate(200);
        setAnchorEl(null);
    };

    const availabilityDataForDisplayDate = findAvailabilityData(
        displayDate,
        availabilityData,
        quantities,
    );

    const dateHasTicketAvailability = availabilityDataForDisplayDate.length > 0;

    const disableOkButton =
        (!displayTimeslot && !dateRangeVariant) ||
        (dateRangeVariant === 'nights' && selectedDateRange?.includes(null));

    const isTimeslot =
        displayTimeslot?.product?.product?.type === 'timeslot' ||
        availabilityData[0]?.product?.type === 'timeslot' ||
        !!availableTimeslots;

    return (
        <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            sx={
                {
                    zIndex: `${zIndex.alwaysVisible} !important`,

                    '& > .MuiPaper-root': {
                        maxWidth: showWeekNumbers ? 365 : 325,
                    },
                } as SystemStyleObject
            }
            container={mountPoint.popover}
            anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
            transformOrigin={{ vertical: 'center', horizontal: 'center' }}
            onClose={onClose}
            aria-modal={true}
            disableEnforceFocus={true}
            ref={popoverRef}
        >
            <DateRangePickerOrCalendar
                dateRangeVariant={dateRangeVariant}
                selectedDateRange={selectedDateRange}
                onSelectDateRange={onSelectDateRange}
                updateAvailabilitySearchPeriod={updateAvailabilitySearchPeriod}
                isLoading={isLoading}
                minDate={minDate}
                threeYearsAhead={threeYearsAhead}
                displayDate={displayDate}
                onSelectDay={onSelectDay}
                availabilityDataForDisplayDate={availabilityDataForDisplayDate}
                displayTimeslot={displayTimeslot}
                setDisplayTimeslot={setDisplayTimeslot}
                availabilityData={availabilityData}
                availableTimeslots={availableTimeslots}
                timeslotSelectorVisible={timeslotSelectorVisible}
                setTimeslotSelectorVisible={setTimeslotSelectorVisible}
                quantities={quantities}
                showWeekNumbers={showWeekNumbers}
            />

            <Box pt={1} pb={2}>
                <Grid container justifyContent="right" pl={2} pr={2.5} gap={1}>
                    {dateHasTicketAvailability ? (
                        <>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => {
                                    onSelectTimeSlot?.(undefined);
                                    setSelectedProducts?.(undefined);
                                    setHasChosenDate(false);
                                    onClose();
                                }}
                                role={capitalize(t.select_date)}
                            >
                                {t.clear}
                            </Button>
                            <Button
                                sx={{
                                    '&:hover': {
                                        backgroundColor: darken(theme.palette.primary.main, 0.2),
                                    },
                                }}
                                variant="contained"
                                color="primary"
                                disabled={disableOkButton}
                                onClick={() =>
                                    isTimeslot && !timeslotSelectorVisible
                                        ? setTimeslotSelectorVisible(true)
                                        : onOkClicked(
                                              dateRangeVariant,
                                              selectedDateRange,
                                              displayTimeslot,
                                              onSelectTimeSlot,
                                              setSelectedProducts,
                                              getAllDatesInDateRange,
                                              availabilityData,
                                              findAvailabilityData,
                                              onClose,
                                              quantities,
                                              setTimeslotSelectorVisible,
                                          )
                                }
                                role={capitalize(t.select_date)}
                            >
                                {t.ok.toUpperCase()}
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                if (!displayDate) return;

                                const firstAvailableProduct = findFirstAvailableProductForDate(
                                    displayDate,
                                    availabilityData,
                                );
                                if (!firstAvailableProduct) return;

                                onSelectTimeSlot?.(undefined);
                                setSelectedProducts?.([firstAvailableProduct]);
                                updateDisplayDate(firstAvailableProduct.start);
                                setHasChosenDate(true);
                                onClose();
                            }}
                        >
                            {t.see_available_tickets}
                        </Button>
                    )}
                </Grid>
            </Box>
        </Popover>
    );
}

function DateRangePickerOrCalendar(props: {
    dateRangeVariant: 'days' | 'nights' | undefined;
    selectedDateRange: DateRange<TZDate> | undefined;
    onSelectDateRange:
        | ((date: DateRange<any>, keyboardInputValue?: string | undefined) => void)
        | undefined;
    updateAvailabilitySearchPeriod: (date: TZDate | null) => void;
    minDate: TZDate | undefined;
    threeYearsAhead: TZDate;
    displayDate: TZDate | null;
    onSelectDay: (date: TZDate | null) => void;
    availabilityDataForDisplayDate: ProductInstance[] | undefined;
    displayTimeslot: TimeSlotType | undefined;
    setDisplayTimeslot: React.Dispatch<React.SetStateAction<TimeSlotType | undefined>>;
    availabilityData: ProductInstance[];
    availableTimeslots?: Timeslot[];
    timeslotSelectorVisible: boolean;
    setTimeslotSelectorVisible: React.Dispatch<React.SetStateAction<boolean>>;
    quantities: TicketOptionWithQuantity[];
    isLoading?: boolean;
    showWeekNumbers: boolean;
}) {
    const {
        availabilityData,
        availabilityDataForDisplayDate,
        dateRangeVariant,
        displayDate,
        displayTimeslot,
        minDate,
        onSelectDateRange,
        onSelectDay,
        selectedDateRange,
        setDisplayTimeslot,
        threeYearsAhead,
        updateAvailabilitySearchPeriod,
        timeslotSelectorVisible,
        quantities,
        setTimeslotSelectorVisible,
        isLoading,
        showWeekNumbers,
    } = props;

    const { t } = useLocale();
    const isTimeslot =
        displayTimeslot?.product?.product?.type === 'timeslot' ||
        availabilityData[0]?.product?.type === 'timeslot';

    const shouldShowNoAvailabilityMessage =
        !isLoading &&
        displayDate &&
        availabilityDataForDisplayDate &&
        availabilityDataForDisplayDate.length === 0;

    const availabilityDataDays = useMemo(() => {
        const now = Date.now();

        const slotsByDate = availabilityData.reduce((acc, cur) => {
            const dateKey = cur.start.format('YYYY-MM-DD');
            if (!acc.has(dateKey)) {
                acc.set(dateKey, []);
            }
            acc.get(dateKey)!.push(cur);
            return acc;
        }, new Map<string, typeof availabilityData>());

        return Array.from(slotsByDate.entries()).reduce((acc, [dateKey, daySlots]) => {
            const hasAvailableSlots = daySlots.some(
                (slot) =>
                    slot.capacity > 0 &&
                    !slot.isClosedForBooking &&
                    (slot.cutoffDate ?? slot.start).isAfter(now),
            );
            const hasWaitlistSlots = daySlots.some(
                (slot) =>
                    (slot as ProductInstance & { hasWaitingList?: boolean }).hasWaitingList ===
                        true && slot.isClosedForBooking,
            );

            if (hasAvailableSlots && hasWaitlistSlots) {
                acc.set(dateKey, 'mixed');
            } else if (hasWaitlistSlots && !hasAvailableSlots) {
                acc.set(dateKey, 'warning');
            } else if (hasAvailableSlots && !hasWaitlistSlots) {
                acc.set(dateKey, 'available');
            }

            return acc;
        }, new Map<string, 'available' | 'warning' | 'mixed'>());
    }, [availabilityData]);

    const showLegend = Array.from(availabilityDataDays.values()).some(
        (value) => value === 'warning' || value === 'mixed',
    );

    if (dateRangeVariant) {
        if (selectedDateRange && onSelectDateRange) {
            const handleDateRangeChange = (
                newDateRange: DateRange<Dayjs | TZDate>,
                _context?: any,
            ) => {
                onSelectDateRange(convertDateRangeToTZDate(newDateRange));
            };

            const handleMonthChangeWrapper = (date: Dayjs | TZDate | null) => {
                const tzDate = date && (date instanceof TZDate ? date : tzdate(date));
                updateAvailabilitySearchPeriod(tzDate);
            };

            return (
                <StaticDateRangePicker
                    date={displayDate?.toISOString()} // Used for default month when nothing selected
                    dateRangeVariant={dateRangeVariant}
                    dateRange={selectedDateRange}
                    onChange={handleDateRangeChange}
                    onMonthChange={handleMonthChangeWrapper}
                    minDate={minDate}
                    maxDate={threeYearsAhead}
                    isDateUnavailable={(date: string) => {
                        return isDateUnavailable(date, availabilityDataDays);
                    }}
                    loading={isLoading}
                    showWeekNumbers={showWeekNumbers}
                    weekNumberTopPadding={96}
                />
            );
        } else return null;
    }

    return (
        <>
            <CalendarHeader
                onClick={
                    !isTimeslot || !timeslotSelectorVisible
                        ? undefined
                        : () => setTimeslotSelectorVisible(false)
                }
                selectedDate={displayDate}
            />
            {!timeslotSelectorVisible && (
                <>
                    {showLegend && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 2,
                                px: 2,
                                pb: 0,
                                pt: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        bgcolor: '#f5f5f5',
                                        border: '1px solid #e0e0e0',
                                    }}
                                />
                                <Typography variant="body2">{t.available}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        bgcolor: (theme) => theme.palette.warning.main,
                                        color: (theme) => theme.palette.warning.contrastText,
                                    }}
                                />
                                <Typography variant="body2">{t.waiting_list}</Typography>
                            </Box>
                        </Box>
                    )}

                    <BilberryStaticCalendar
                        date={displayDate}
                        onChange={onSelectDay}
                        onMonthChange={updateAvailabilitySearchPeriod}
                        onYearChange={updateAvailabilitySearchPeriod}
                        minDate={minDate}
                        maxDate={threeYearsAhead}
                        shouldDisableDate={(date) => isDateUnavailable(date, availabilityDataDays)}
                        highlightedDates={availabilityDataDays}
                        loading={isLoading}
                        showWeekNumbers={showWeekNumbers}
                    />

                    {!isTimeslot && (
                        <>
                            <InstancePicker
                                availabilityData={availabilityDataForDisplayDate}
                                selectedTimeSlot={displayTimeslot ?? undefined}
                                onSelectTimeSlot={setDisplayTimeslot}
                            />
                            {shouldShowNoAvailabilityMessage && (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography sx={{ fontStyle: 'italic', fontSize: 'smaller' }}>
                                        {t.some_tickets_are_unavailable_the_selected_day}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}
            {timeslotSelectorVisible && (
                <Slide direction="left" in={timeslotSelectorVisible}>
                    <Box
                        sx={(theme) => ({
                            padding: theme.spacing(2, 0),
                            width: '325px',
                            height: '385px',
                            overflow: 'auto',
                        })}
                    >
                        <BookingTimeslots
                            onSelectTimeslot={setDisplayTimeslot}
                            selectedTimeslot={displayTimeslot}
                            productInstance={displayTimeslot?.product}
                            participants={quantities.reduce((acc, cur) => acc + cur.quantity, 0)}
                        />
                    </Box>
                </Slide>
            )}
        </>
    );
}

function onOkClicked(
    dateRangeVariant: 'days' | 'nights' | undefined,
    selectedDateRange: DateRange<TZDate> | undefined,
    displayTimeslot: TimeSlotType | undefined,
    onSelectTimeSlot: ((timeslot: TimeSlotType) => void) | undefined,
    setSelectedProducts: ((product: any[] | undefined) => void) | undefined,
    getAllDatesInDateRange: (arg0: DateRange<TZDate> | undefined) => any[],
    availabilityData: ProductInstance[],
    findAvailabilityDataNew: (arg0: any, arg1: any, arg2: any) => any[],
    onClose: () => void,
    quantities: TicketOptionWithQuantity[],
    setTimeslotSelectorVisible: React.Dispatch<React.SetStateAction<boolean>>,
) {
    setTimeslotSelectorVisible(false);
    if (!dateRangeVariant) {
        setSelectedProducts?.([displayTimeslot!.product]);
        if (onSelectTimeSlot) {
            onSelectTimeSlot(displayTimeslot!);
        }
    } else {
        // Allow selecting a single day in the date range picker. Represent this by copying the start date into the end date.
        const dateRange =
            dateRangeVariant === 'days' && !!selectedDateRange?.[0] && !selectedDateRange?.[1]
                ? ([selectedDateRange[0], selectedDateRange[0]] as DateRange<TZDate>)
                : selectedDateRange;
        setSelectedProducts?.(
            getAllDatesInDateRange(dateRange)
                .map((date: any) => findAvailabilityDataNew(date, availabilityData, quantities)[0])
                // Handle the case where the end date is not available by copying the product (since its' otherwise undefined), and modifying start and end.
                .map(
                    (x, i, arr) =>
                        x ?? {
                            ...arr[i - 1],
                            start: arr[i - 1].start.add(1, 'day'),
                            end: arr[i - 1].end.add(1, 'day'),
                        },
                ),
        );
    }
    onClose();
}

function isDateUnavailable(date: string | null, availabilityMap: Map<string, string>): boolean {
    if (!date) return false;
    return !availabilityMap.has(date);
}
